/**
 * PR33 (Launch & Business Readiness): the smallest maintainable mechanism
 * for tagging every analytics event with which distribution channel a
 * build was served from - deliberately build/config-based (`VITE_
 * DISTRIBUTION_CHANNEL`, read once at build time), never inferred from
 * `document.referrer` or any other runtime signal a portal wrapper could
 * spoof or that could vary within a single build. This PR wires only
 * `"direct"` (today's only real channel, and the safe default for an
 * unset/unrecognized value); `"crazygames"`/`"poki"` are typed here so a
 * future portal integration is just a build-time env var value, with no
 * SDK/gameplay change and no code here to revisit.
 *
 * Pure and framework-free on purpose (same pattern as
 * `shouldEnableAnalytics.ts`): takes the raw env value as a plain
 * argument rather than reading `import.meta.env` itself, so this exact
 * decision is unit-testable without a Vite env. See `posthogClient.ts`
 * for the one call site that reads the real env var and registers the
 * resolved channel as a PostHog super property.
 */
export type DistributionChannel = "direct" | "crazygames" | "poki";

const KNOWN_DISTRIBUTION_CHANNELS: ReadonlySet<string> = new Set<DistributionChannel>(["direct", "crazygames", "poki"]);

export function resolveDistributionChannel(raw: string | null | undefined): DistributionChannel {
  if (raw && KNOWN_DISTRIBUTION_CHANNELS.has(raw)) return raw as DistributionChannel;
  return "direct";
}
